import { useState } from "react";
import type { MicrophonePermission } from "../../../../packages/domain/src/index.js";
import { decideSpeechCapture, decideTtsOutput } from "../../../../packages/domain/src/index.js";
import type { MaybePromise } from "../community/types.js";

export interface AgenticTripContextSummary {
  readonly tripId: string;
  readonly name: string;
  readonly destination: string;
}

export interface AgenticPanelCapabilities {
  readonly text: boolean;
  readonly speechToText: boolean;
  readonly textToSpeech: boolean;
  readonly translation: boolean;
  readonly supportEscalation: boolean;
  readonly unavailableReason: string | null;
}

export interface AgenticPanelCallbacks {
  readonly onAskTyped: (input: { readonly prompt: string; readonly tripId: string | null; readonly targetLocale: string | null }) => MaybePromise<void>;
  readonly onStartPushToTalk: (input: { readonly tripId: string | null; readonly permission: MicrophonePermission }) => MaybePromise<void>;
  readonly onStopPushToTalk?: (input: { readonly requestId: string | null }) => MaybePromise<void>;
  readonly onSubmitVoiceTranscript?: (input: { readonly prompt: string; readonly tripId: string | null; readonly targetLocale: string | null; readonly sttRequestId: string | null }) => MaybePromise<void>;
  readonly onRequestVoiceOutput: (input: { readonly text: string; readonly disclosureAcknowledged: boolean }) => MaybePromise<void>;
  readonly onPlayVoiceOutput?: (input: { readonly requestId: string; readonly encryptedAudioReference: string }) => MaybePromise<void>;
  readonly onCancelVoiceOutput?: (input: { readonly requestId: string }) => MaybePromise<void>;
  readonly onEscalateSupport: (input: { readonly summary: string; readonly replyEmail: string }) => MaybePromise<void>;
}

export type AgenticVoiceCaptureStatus = "IDLE" | "CAPTURING" | "UPLOADING" | "TRANSCRIBING" | "TRANSCRIBED" | "ERROR";
export type AgenticVoiceOutputStatus = "IDLE" | "QUEUED" | "READY" | "PLAYING" | "CANCELLED" | "ERROR";

export interface AgenticVoiceCaptureState {
  readonly requestId: string | null;
  readonly status: AgenticVoiceCaptureStatus;
  readonly transcript: string | null;
  readonly error: string | null;
  readonly typedFallbackAvailable: boolean;
  readonly rawAudioRetained: false;
}

export interface AgenticVoiceOutputState {
  readonly requestId: string | null;
  readonly status: AgenticVoiceOutputStatus;
  readonly encryptedAudioReference: string | null;
  readonly expiresAt: string | null;
  readonly error: string | null;
  readonly typedFallbackAvailable: boolean;
}

export interface AgenticTravelCompanionPanelProps {
  readonly openedByUser: boolean;
  readonly accountTier: "FREE" | "INSIDER" | "CREATOR" | "LIFETIME_CREATOR";
  readonly trips: readonly AgenticTripContextSummary[];
  readonly capabilities: AgenticPanelCapabilities;
  readonly microphonePermission: MicrophonePermission;
  readonly lastAnswer: string | null;
  readonly voiceCapture?: AgenticVoiceCaptureState;
  readonly voiceOutput?: AgenticVoiceOutputState;
  readonly callbacks: AgenticPanelCallbacks;
}

function selectedTripId(data: FormData): string | null {
  const value = String(data.get("tripId") ?? "");
  return value.length === 0 ? null : value;
}

export function AgenticTravelCompanionPanel(props: AgenticTravelCompanionPanelProps) {
  const [targetLocale, setTargetLocale] = useState("en-US");
  const [voiceTripId, setVoiceTripId] = useState("");
  const speech = decideSpeechCapture({ userPressedMicrophone: true, permission: props.microphonePermission });
  const voice = decideTtsOutput({
    userOptedIn: true,
    providerAvailable: props.capabilities.textToSpeech,
    disclosureAcknowledged: false
  });
  if (!props.openedByUser) return null;
  return <section className="section card" aria-labelledby="agentic-companion-title">
    <div className="section-heading">
      <div><span className="eyebrow">AGENTIC</span><h3 id="agentic-companion-title">Travel companion</h3></div>
      <span className="badge">Full access for {props.accountTier.replaceAll("_", " ").toLowerCase()}</span>
    </div>
    {props.capabilities.unavailableReason === null ? null : <p role="status" className="notice">{props.capabilities.unavailableReason}</p>}
    <form className="form" onSubmit={(event) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      void props.callbacks.onAskTyped({ prompt: String(data.get("prompt") ?? ""), tripId: selectedTripId(data), targetLocale });
    }}>
      <label className="field">Trip context<select name="tripId" defaultValue="">
        <option value="">General travel question</option>
        {props.trips.map((trip) => <option key={trip.tripId} value={trip.tripId}>{trip.name} · {trip.destination}</option>)}
      </select></label>
      <label className="field">Ask Portability<textarea name="prompt" required maxLength={4_000} /></label>
      <label className="field">Answer language<input value={targetLocale} onChange={(event) => setTargetLocale(event.currentTarget.value)} /></label>
      <button className="primary" type="submit" disabled={!props.capabilities.text}>Ask</button>
    </form>
    {props.voiceCapture === undefined ? null : <article className="card" aria-labelledby="agentic-voice-capture-title">
      <h4 id="agentic-voice-capture-title">Voice capture</h4>
      <p role="status">Status: {props.voiceCapture.status.toLowerCase().replaceAll("_", " ")}</p>
      <p className="microcopy">Audio uses encrypted transport and transient provider processing; raw audio retention is {props.voiceCapture.rawAudioRetained ? "enabled" : "disabled"}.</p>
      <label className="field">Voice trip context<select value={voiceTripId} onChange={(event) => setVoiceTripId(event.currentTarget.value)}>
        <option value="">General travel question</option>
        {props.trips.map((trip) => <option key={trip.tripId} value={trip.tripId}>{trip.name} · {trip.destination}</option>)}
      </select></label>
      {props.voiceCapture.transcript === null ? <p>{props.voiceCapture.typedFallbackAvailable ? "Typed fallback is available while voice is unavailable." : "No transcript yet."}</p> : <form className="form" onSubmit={(event) => {
        event.preventDefault();
        void props.callbacks.onSubmitVoiceTranscript?.({ prompt: props.voiceCapture?.transcript ?? "", tripId: voiceTripId.length === 0 ? null : voiceTripId, targetLocale, sttRequestId: props.voiceCapture?.requestId ?? null });
      }}>
        <label className="field">Verified transcript<textarea readOnly value={props.voiceCapture.transcript} /></label>
        <button className="secondary" type="submit" disabled={props.callbacks.onSubmitVoiceTranscript === undefined}>Ask with transcript</button>
      </form>}
      {props.voiceCapture.error === null ? null : <p role="alert" className="notice">{props.voiceCapture.error}</p>}
    </article>}
    <div className="button-row">
      <button className="secondary" type="button" disabled={!props.capabilities.speechToText || props.microphonePermission === "DENIED"} onClick={() => void props.callbacks.onStartPushToTalk({ tripId: null, permission: props.microphonePermission })}>Push to talk</button>
      <button className="secondary" type="button" disabled={props.callbacks.onStopPushToTalk === undefined || props.voiceCapture?.status !== "CAPTURING"} onClick={() => void props.callbacks.onStopPushToTalk?.({ requestId: props.voiceCapture?.requestId ?? null })}>Stop recording</button>
      <button className="secondary" type="button" disabled={!props.capabilities.textToSpeech || props.lastAnswer === null} onClick={() => void props.callbacks.onRequestVoiceOutput({ text: props.lastAnswer ?? "", disclosureAcknowledged: true })}>Read answer aloud</button>
    </div>
    {props.voiceOutput === undefined ? null : <article className="card" aria-labelledby="agentic-voice-output-title">
      <h4 id="agentic-voice-output-title">Voice output</h4>
      <p role="status">Status: {props.voiceOutput.status.toLowerCase()}</p>
      {props.voiceOutput.expiresAt === null ? null : <p className="microcopy">Audio access expires {props.voiceOutput.expiresAt}.</p>}
      <div className="button-row">
        <button className="secondary compact" type="button" disabled={props.voiceOutput.status !== "READY" || props.voiceOutput.requestId === null || props.voiceOutput.encryptedAudioReference === null || props.callbacks.onPlayVoiceOutput === undefined} onClick={() => void props.callbacks.onPlayVoiceOutput?.({ requestId: props.voiceOutput?.requestId ?? "", encryptedAudioReference: props.voiceOutput?.encryptedAudioReference ?? "" })}>Play voice answer</button>
        <button className="secondary compact" type="button" disabled={props.voiceOutput.requestId === null || props.voiceOutput.status === "CANCELLED" || props.callbacks.onCancelVoiceOutput === undefined} onClick={() => void props.callbacks.onCancelVoiceOutput?.({ requestId: props.voiceOutput?.requestId ?? "" })}>Cancel voice</button>
      </div>
      {props.voiceOutput.error === null ? null : <p role="alert" className="notice">{props.voiceOutput.error}</p>}
      {props.voiceOutput.typedFallbackAvailable ? <p className="microcopy">Typed answer remains available if playback fails.</p> : null}
    </article>}
    <p className="microcopy">
      Microphone access is requested only after tapping push-to-talk. {speech.requestPermissionInContext ? "The next tap may request microphone permission." : speech.captureAllowed ? "Push-to-talk can capture transient audio." : "Typed questions remain available."}
    </p>
    <p className="microcopy">
      Voice output is optional and disclosed. {voice.reason === "PROVIDER_UNAVAILABLE" ? "The voice provider is not active yet." : "Text answers remain the authority when voice is unavailable."}
    </p>
    {props.lastAnswer === null ? <p>No answer yet.</p> : <article className="card"><h4>Latest answer</h4><p>{props.lastAnswer}</p></article>}
    <form className="form" onSubmit={(event) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      void props.callbacks.onEscalateSupport({ summary: String(data.get("summary") ?? ""), replyEmail: String(data.get("replyEmail") ?? "") });
    }}>
      <h4>Escalate serious issue</h4>
      <label className="field">Reply email<input name="replyEmail" type="email" required /></label>
      <label className="field">Summary<textarea name="summary" required maxLength={2_000} /></label>
      <button className="secondary" type="submit" disabled={!props.capabilities.supportEscalation}>Create support case</button>
    </form>
    <p className="microcopy">Agentic cannot book, buy, save, claim, change trips, or perform consequential actions by itself. Use the normal controls for those actions.</p>
  </section>;
}
